import { IRefIds } from 'constants/refs';
import { del, get, notifyError, postFile, put } from 'services/request';
import { BaseModel, IModelCourseOptions, IModuleSearch } from './BaseModel';
import { IAudioSetsItemReviewResult } from './AudioSetsItemModel';

type IAudioSet = {
  id: number;
  artist: string;
  date: string;
  n: number;
  nGood: number;
  nRepeat: number;
  nProgress: string;
  ref: number;
  revision: number;
  speed: number;
  statusRecording: number;
  setName: string;
  words: number;
};

export type IAudioSetEdit = {
  id: number;
  statusRecording: number;
  readBy: 'm' | 'f' | 'mf';
  ssmlCode: string;
  ttsEnabled: boolean;
  isTts: boolean;
  ttsVoiceM: string;
  ttsVoiceF: string;
  ttsPrompt: string;
  deletable: boolean;
};

type IAudioTTSStart = {
  result: string;
  error?: string;
  pending: number[];
};

type IAudioItemTTSResult = {
  result: string;
  error?: string;
  audioPath?: string;
};

type IAudioDeleteRangeResult = {
  result: string;
  audioPath: string;
  durationMs: number;
  error?: string;
};

export type IAudioTTSProgress = {
  setId: number;
  total: number;
  done: number;
  errors: string[];
};

/** Items generated at once while voicing a set (Gemini TTS is ~2-3 s per clip). */
const TTS_CONCURRENCY = 2;

type IAudioSetSearch = IModuleSearch & {
  dialect: number;
};

type IAudioUpload = {
  status: number;
  error: string;
};

export default class AudioSetsModel extends BaseModel<IAudioSet, IAudioSetEdit> {
  constructor(options: IModelCourseOptions) {
    super(options);
    this.ref = IRefIds.audio;
    this.gridName = 'AudioSet';
  }

  async getGridSearch(code: string, dialect: number) {
    await super.getGrid();
    this.render();

    if (this.gridColumns.length === 0) {
      await this.getGridColumns();
    }

    this.gridData = await get<IAudioSet[]>(`/courses/${this.courseId}/sets?dialect=${dialect}&code=${code}`);

    this.loadingGrid = false;
    this.render();
  }

  async getGridRow(itemId: number) {
    return await get<IAudioSet>(`/courses/${this.courseId}/sets/${itemId}`);
  }

  async delete() {
    await super.delete();
    const { id } = this.itemUpdated as IAudioSetEdit;

    const result = await del(`audio-sets/${id}`);

    if (result.ok) {
      await this.updateGridRow(id, 'remove');
      this.itemUpdated = null;
      this.loadingItem = false;
      this.render();
    } else {
      console.error(JSON.stringify(result.data));
    }
  }

  async loadItemData(itemId: number) {
    return await get<IAudioSetEdit>(`audio-sets/${itemId}`);
  }

  getDownloadLink(setId: number) {
    return `audio-sets/${setId}/download`;
  }

  async rejectSet(setId: number) {
    await super.save();
    await put(`audio-sets/${setId}/reject`, {});
    await Promise.all([this.updateGridRow(setId, 'update'), this.loadItemEdit(setId)]);
  }

  async uploadFile(file: File) {
    const response = await postFile<IAudioUpload>(`audio-sets/${this.itemId}/upload`, file);
    await Promise.all([this.updateGridRow(this.itemId, 'update'), this.loadItemEdit(this.itemId)]);

    return response.data;
  }

  /** Progress of the background TTS voicing of a set (null = idle). */
  ttsProgress: IAudioTTSProgress | null = null;

  /** Set item ids being (re)generated right now (redo from the review modal). */
  regenerating: Set<number> = new Set();

  /**
   * Launches the TTS voicing: stores voices + prompt on the set and voices the pending items
   * one by one in the background (the modal closes right away; the status row shows progress).
   */
  async startTTS(nameM: string, nameF: string, prompt: string, onItemDone: (itemId: number) => Promise<void>) {
    const setId = this.itemId;
    const response = await put<IAudioTTSStart>(`audio-sets/${setId}/tts`, { voiceM: nameM, voiceF: nameF, prompt });
    if (!response.ok || !response.data || response.data.result !== 'OK') {
      notifyError('TTS: ' + (response.data?.error || 'could not launch'));
      return;
    }
    await this.loadItemEdit(setId);
    const pending = response.data.pending;
    this.ttsProgress = { setId, total: pending.length, done: 0, errors: [] };
    this.render();

    const queue = [...pending];
    const worker = async () => {
      while (queue.length > 0) {
        const itemId = queue.shift() as number;
        const error = await this.generateItem(itemId, '');
        if (this.ttsProgress) {
          this.ttsProgress.done++;
          if (error) {
            this.ttsProgress.errors.push(error);
          }
        }
        this.render();
        await onItemDone(itemId);
      }
    };
    await Promise.all(Array.from({ length: TTS_CONCURRENCY }, worker));

    const errors = this.ttsProgress ? this.ttsProgress.errors : [];
    this.ttsProgress = null;
    if (errors.length > 0) {
      notifyError(`TTS: ${errors.length} item(s) failed — ${errors[0]}`);
    }
    await Promise.all([this.updateGridRow(setId, 'update'), this.loadItemEdit(setId)]);
  }

  /**
   * Voices ONE set item (initial voicing or redo with an adjusted prompt). With ipaWord+ipa the item is
   * voiced by Chirp 3 HD (same voice) forcing that word's pronunciation. With readAs that text is voiced instead of
   * the item's, which stays as written in the course. Returns the error message, or '' on success.
   */
  async generateItem(itemId: number, prompt: string, ipaWord = '', ipa = '', readAs = ''): Promise<string> {
    this.regenerating.add(itemId);
    this.render();
    let error = '';
    try {
      const response = await put<IAudioItemTTSResult>(`/audio-sets/items/${itemId}/tts`, { prompt, ipaWord, ipa, readAs });
      if (!response.ok || !response.data || response.data.result !== 'OK') {
        error = response.data?.error || 'generation failed';
      }
    } finally {
      this.regenerating.delete(itemId);
      this.render();
    }
    return error;
  }

  async deleteItemAudioRange(itemId: number, startMs: number, endMs: number) {
    const response = await put<IAudioDeleteRangeResult>(`/audio-sets/items/${itemId}/audio/delete-range`, {
      startMs,
      endMs,
    });
    if (!response.ok || response.data?.result !== 'OK') {
      notifyError(response.data?.error || 'Could not edit the audio');
      return null;
    }
    return response.data;
  }

  /** Redo from the review modal: fire-and-forget, the caller moves on to the next item. */
  redoItem(
    itemId: number,
    prompt: string,
    onDone: (itemId: number) => Promise<void>,
    ipaWord = '',
    ipa = '',
    readAs = '',
  ) {
    this.generateItem(itemId, prompt, ipaWord, ipa, readAs).then(async (error) => {
      if (error) {
        notifyError('TTS redo failed: ' + error);
      }
      await onDone(itemId);
      await Promise.all([this.updateGridRow(this.itemId, 'update'), this.loadItemEdit(this.itemId)]);
    });
  }

  async reviewItem(itemId: number, ok: boolean, note: string) {
    const result = await put<IAudioSetsItemReviewResult>(`/audio-sets/items/${itemId}/review`, { ok, note });
    await Promise.all([this.updateGridRow(this.itemId, 'update'), this.loadItemEdit(this.itemId)]);
    return result.data;
  }

  async search(data: IAudioSetSearch) {
    await this.getGridSearch(data.text || '', data.dialect);
  }
}
